// Shopify theme sync marker: listing-rewrite-2026-06-21.
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { createSoAtlantaClient, normalizeBusinesses } from '@theme/so-atlanta-api';
import { escapeAttr, escapeHtml, renderEmpty, renderError, renderTrustBadges } from '@theme/so-atlanta-renderers';
import { venueProfileUrl } from '@theme/so-atlanta-urls';

const MAPBOX_GL_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.js';
const MAPBOX_GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.css';

const SPOT_ZOOM = 16;
const MAP_PITCH = 69;

/** 90 days in ms — listings without `last_updated` in range are hidden when freshness filter is on */
const FRESH_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

/** GeoJSON source + layer ids for clustered venue pins */
const DISCOVERY_SOURCE_ID = 'discovery-spots';
const LAYER_CLUSTERS = 'discovery-spots-clusters';
const LAYER_CLUSTER_COUNT = 'discovery-spots-cluster-count';
const LAYER_UNCLUSTERED_DOT = 'discovery-spots-unclustered-dot';
const LAYER_UNCLUSTERED = 'discovery-spots-unclustered';
const LAYER_UNCLUSTERED_LABEL = 'discovery-spots-unclustered-label';
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 50;
const LABEL_MIN_ZOOM = 15;
const LIST_BATCH_SIZE = 12;
const FALLBACK_PIN_IMAGE_ID = 'discovery-pin-fallback';
const MAP_LAYER_SLOT = 'top';

/**
 * @typedef {object} DiscoverySpot
 * @property {string} handle
 * @property {string} name
 * @property {string} title - alias for name, kept for backward compatibility
 * @property {string} businessName - alias for headline
 * @property {string} logoImage
 * @property {string|undefined} logoUrl
 * @property {string} fallbackImage
 * @property {string|undefined} imageUrl
 * @property {any} featuredVideo
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} address
 * @property {string|undefined} neighborhood
 * @property {string[]|undefined} categories
 * @property {string[]|undefined} businessTypes
 * @property {string[]|undefined} vibes
 * @property {string[]|undefined} neighborhoods
 * @property {string[]|undefined} platformTags
 * @property {string[]|undefined} profileChips
 * @property {string[]|undefined} knownFor
 * @property {string[]|undefined} goodFor
 * @property {string|undefined} whyGo
 * @property {string|undefined} visitSignal
 * @property {string|undefined} locationSummary
 * @property {string|undefined} description
 * @property {string|undefined} shortDescription
 * @property {string|undefined} socialPullText
 * @property {string|undefined} hoursText
 * @property {string|undefined} parkingNotes
 * @property {string} category - display string, derived from categories[]
 * @property {boolean} featured
 * @property {string|null|undefined} lastContributionAt ISO date string YYYY-MM-DD
 * @property {string|null|undefined} lastUpdated - alias for lastContributionAt
 * @property {number} sortOrder
 * @property {string} instagramUrl
 * @property {string} hostedVideoUrl
 * @property {string|undefined} slug
 * @property {string|undefined} primaryCtaText
 * @property {string|undefined} primaryCtaUrl
 * @property {string} ctaText
 * @property {string} ctaUrl
 * @property {number|null} scoreOverall
 * @property {number|null} recentContributionCount
 */

class DiscoveryMapComponent extends Component {
  /** @type {any} */
  map = null;

  /** @type {DiscoverySpot[]} */
  spots = [];

  /** @type {any} */
  settings = null;

  /** @type {ReturnType<typeof createSoAtlantaClient> | null} */
  client = null;

  /** Whether current spot/list data came from the Replit API */
  apiDataActive = false;

  /** @type {string|null} */
  activeSpotHandle = null;

  /** Normalized lowercase search string */
  searchQuery = '';

  /** When true, only spots with lastUpdated within the last 90 days are shown */
  freshOnly = false;

  /** When true, only confirmed-open place listings are loaded/shown */
  openNowOnly = false;

  /** Tracks whether the component is currently in the DOM */
  #isConnected = false;

  #visibleListCount = LIST_BATCH_SIZE;

  /** @type {string[]} */
  #filteredListHandles = [];

  /** @type {IntersectionObserver | null} */
  #listLoadObserver = null;

  /** @type {HTMLDivElement | null} */
  #listLoadSentinel = null;

  #syncRunId = 0;

  /** @type {Map<string, Promise<boolean>>} */
  #pendingImageLoads = new Map();

  /** @type {Set<string>} */
  #loadedImageIds = new Set();

  /** @type {Set<string>} */
  #failedLogoImageIds = new Set();

  /** @type {{ latitude: number, longitude: number } | null} */
  #userLocation = null;

  #debouncedApplyDiscoveryFilters = debounce(() => this.#applyDiscoveryFilters(), 200);

  /** @param {any} e */
  #onClusterClick = (e) => {
    if (!this.map) return;
    const features = this.map.queryRenderedFeatures(e.point, { layers: [LAYER_CLUSTERS] });
    if (!features.length) return;
    const props = features[0].properties;
    const clusterId = props?.cluster_id;
    const geom = features[0].geometry;
    if (clusterId == null || !geom || geom.type !== 'Point') return;
    const coords = /** @type {{ coordinates: [number, number] }} */ (geom).coordinates;
    const source = this.map.getSource(DISCOVERY_SOURCE_ID);
    if (!source || typeof source.getClusterExpansionZoom !== 'function') return;
    const clusterIdNum = typeof clusterId === 'number' ? clusterId : Number(clusterId);
    if (Number.isNaN(clusterIdNum)) return;
    source.getClusterExpansionZoom(clusterIdNum, (/** @type {Error | null} */ err, /** @type {number} */ zoom) => {
      if (err || !this.map) return;
      this.map.easeTo({
        center: coords,
        zoom,
        duration: 450,
      });
    });
  };

  /** @param {any} e */
  #onUnclusteredClick = (e) => {
    const f = e.features?.[0];
    const handle = f?.properties?.handle;
    if (handle == null) return;
    this.#previewSpot(String(handle), { fly: true });
  };

  #onSpotsLayerMouseEnter = () => {
    if (this.map) this.map.getCanvas().style.cursor = 'pointer';
  };

  #onSpotsLayerMouseLeave = () => {
    if (this.map) this.map.getCanvas().style.cursor = '';
  };

  /** @param {unknown} ref */
  #refsToInputArray(ref) {
    if (ref == null) return [];
    if (Array.isArray(ref)) return ref.filter((x) => x instanceof HTMLInputElement);
    if (ref instanceof HTMLInputElement) return [ref];
    return [];
  }

  /** @param {KeyboardEvent} e */
  #onPreviewKeydown = (e) => {
    if (e.key !== 'Escape') return;
    const panel = this.refs.previewPanel;
    if (!(panel instanceof HTMLElement) || panel.hidden) return;
    e.preventDefault();
    this.handlePreviewClose();
  };

  /** @param {boolean} active */
  #setDiscoveryLoading(active) {
    this.classList.toggle('discovery-map--loading', active);
    this.setAttribute('aria-busy', active ? 'true' : 'false');
  }

  /** @param {string} message */
  #setMapStatus(message) {
    const status = this.refs.mapStatus;
    if (!(status instanceof HTMLElement)) return;
    const text = String(message || '').trim();
    status.hidden = !text;
    status.textContent = text;
  }

  /** @param {DiscoverySpot[]} activeSpots */
  #syncMapStatus(activeSpots) {
    if (!this.spots.length) {
      const listContainer = this.refs.listContainer;
      if (listContainer instanceof HTMLElement && listContainer.getAttribute('aria-busy') === 'true') {
        this.#setMapStatus('');
        return;
      }
      this.#setMapStatus(this.settings?.emptyText || 'No venues on the map yet.');
      return;
    }
    if (!activeSpots.length) {
      this.#hidePreviewPanel();
      this.#setMapStatus('No venues match these filters.');
      return;
    }
    this.#setMapStatus('');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#isConnected = true;
    this.#parseData();
    this.client = createSoAtlantaClient(this.settings || {});
    this.#setupDiscoveryFiltersUI();
    document.addEventListener('keydown', this.#onPreviewKeydown, true);
    void this.#bootstrapDiscoveryMap();
  }

  async #bootstrapDiscoveryMap() {
    await this.#loadApiMapData();

    if (!this.settings?.accessToken) {
      this.#setDiscoveryLoading(false);
      return;
    }

    this.#setDiscoveryLoading(true);

    try {
      await this.#loadMapbox();
      if (this.#isConnected) {
        this.#initMap();
      }
    } catch (e) {
      console.error('Discovery Map: init failed', e);
    } finally {
      if (this.#isConnected) {
        this.#setDiscoveryLoading(false);
      }
    }
  }

  disconnectedCallback() {
    this.#setDiscoveryLoading(false);
    this.#isConnected = false;
    this.#teardownListLazyLoad();
    this.#hidePreviewPanel();
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('keydown', this.#onPreviewKeydown, true);
    if (this.map) {
      try {
        this.map.remove();
      } catch (_) {
        /* map may already be disposed */
      }
      this.map = null;
    }
  }

  #parseData() {
    const settingsEl = this.querySelector('[data-discovery-settings]');

    try {
      this.settings = settingsEl ? JSON.parse(settingsEl.textContent ?? '{}') : null;
    } catch (e) {
      console.error('Discovery Map: Failed to parse settings', e);
    }

    this.spots = [];
  }

  async #loadApiMapData() {
    if (!this.client) return;

    const listContainer = this.refs.listContainer;
    this.#setDiscoveryLoading(true);
    if (listContainer instanceof HTMLElement) {
      listContainer.setAttribute('aria-busy', 'true');
    }

    try {
      const data = await this.client.map(this.openNowOnly ? { openNow: true } : {});
      if (!this.#isConnected) return;
      const businesses = normalizeBusinesses(data?.listings || data?.businesses);
      const spots = businesses
        .filter((b) => b.showOnMap !== false && b.latitude != null && b.longitude != null)
        .map((b, index) => ({
          ...b,
          handle: b.slug || b.shopifyMetaobjectHandle || b.handle || `api-spot-${index}`,
          sortOrder: Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : index,
          category: b.category || (Array.isArray(b.categories) ? b.categories[0] : ''),
          tiktokEmbedUrl: b.featuredVideo?.embedUrl || '',
        }));

      this.apiDataActive = true;
      this.spots = spots;
      this.spots.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      if (listContainer instanceof HTMLElement) {
        listContainer.removeAttribute('aria-busy');
        if (!spots.length) {
          listContainer.innerHTML = renderEmpty(this.settings?.emptyText);
          this.#setMapStatus(this.settings?.emptyText || 'No venues on the map yet.');
        } else {
          this.#renderApiListEntries(spots);
          this.#applyDiscoveryFilters({ resetBatch: true });
        }
      }
    } catch (error) {
      console.error('Discovery Map: API data failed', error);
      this.spots = [];
      this.#setMapStatus(this.settings?.errorText || 'Unable to load map venues.');
      if (listContainer instanceof HTMLElement) {
        listContainer.removeAttribute('aria-busy');
        listContainer.innerHTML = renderError(this.settings?.errorText);
      }
    } finally {
      if (this.#isConnected) {
        this.#setDiscoveryLoading(false);
      }
    }
  }

  #setupDiscoveryFiltersUI() {
    const searchInputs = this.#refsToInputArray(this.refs.discoverySearchInput);
    const toggles = this.#refsToInputArray(this.refs.discoveryFreshToggle);
    const openNowToggles = this.#refsToInputArray(this.refs.discoveryOpenNowToggle);

    if (toggles.length) {
      this.freshOnly = toggles.some((t) => t.checked);
      for (const t of toggles) {
        t.checked = this.freshOnly;
      }
    } else {
      this.freshOnly = false;
    }

    if (openNowToggles.length) {
      this.openNowOnly = openNowToggles.some((t) => t.checked);
      for (const t of openNowToggles) {
        t.checked = this.openNowOnly;
      }
    } else {
      this.openNowOnly = false;
    }

    this.searchQuery = '';
    for (const input of searchInputs) {
      input.value = '';
      input.addEventListener('input', (ev) => {
        const el = ev.target;
        if (!(el instanceof HTMLInputElement)) return;
        this.searchQuery = el.value.trim().toLowerCase();
        for (const other of searchInputs) {
          if (other !== el) other.value = el.value;
        }
        this.#debouncedApplyDiscoveryFilters();
      });
    }

    for (const toggle of toggles) {
      toggle.addEventListener('change', (ev) => {
        const el = ev.target;
        if (!(el instanceof HTMLInputElement)) return;
        this.freshOnly = el.checked;
        for (const other of toggles) {
          if (other !== el) other.checked = el.checked;
        }
        this.#applyDiscoveryFilters();
      });
    }

    for (const toggle of openNowToggles) {
      toggle.addEventListener('change', (ev) => {
        const el = ev.target;
        if (!(el instanceof HTMLInputElement)) return;
        this.openNowOnly = el.checked;
        for (const other of openNowToggles) {
          if (other !== el) other.checked = el.checked;
        }
        void this.#loadApiMapData();
      });
    }

    this.#setupListLazyLoad();
    this.#applyDiscoveryFilters();
  }

  #setupListLazyLoad() {
    const listContainer = this.refs.listContainer;
    if (!(listContainer instanceof HTMLElement)) return;

    if (!this.#listLoadSentinel || this.#listLoadSentinel.parentElement !== listContainer) {
      const sentinel = document.createElement('div');
      sentinel.className = 'discovery-map__list-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      this.#listLoadSentinel = sentinel;
      listContainer.appendChild(sentinel);
    }

    this.#teardownListLazyLoad();

    if (typeof IntersectionObserver !== 'function' || !this.#listLoadSentinel) return;

    this.#listLoadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#revealMoreListItems();
      },
      {
        root: listContainer,
        rootMargin: '0px 0px 160px 0px',
        threshold: 0,
      }
    );

    this.#listLoadObserver.observe(this.#listLoadSentinel);
  }

  #teardownListLazyLoad() {
    if (this.#listLoadObserver) {
      this.#listLoadObserver.disconnect();
      this.#listLoadObserver = null;
    }
  }

  /** @param {DiscoverySpot} spot */
  #isSpotFresh(spot) {
    const raw = spot.lastContributionAt ?? spot.lastUpdated;
    if (raw == null || raw === '') return false;
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw).trim());
    if (!parts) return false;
    const updated = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    updated.setHours(0, 0, 0, 0);
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setTime(cutoff.getTime() - FRESH_WINDOW_MS);
    return updated.getTime() >= cutoff.getTime();
  }

  /** @param {DiscoverySpot} spot @param {string} q normalized lowercase */
  #spotMatchesSearch(spot, q) {
    if (!q) return true;
    const flatten = (/** @type {string[]|undefined} */ arr) =>
      Array.isArray(arr) ? arr.filter(Boolean).join(' ') : '';
    const catHay = flatten(spot.categories) || spot.category || '';
    const hay = [
      spot.name,
      spot.title,
      spot.businessName,
      spot.address,
      spot.neighborhood,
      spot.whyGo,
      spot.visitSignal,
      spot.locationSummary,
      catHay,
      flatten(spot.businessTypes),
      flatten(spot.vibes),
      flatten(spot.neighborhoods),
      flatten(spot.platformTags),
      flatten(spot.profileChips),
      flatten(spot.knownFor),
      flatten(spot.goodFor),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  }

  /** @param {DiscoverySpot} spot */
  #spotMatchesDiscoveryFilters(spot) {
    if (this.openNowOnly && spot.openNow !== true) return false;
    if (this.freshOnly && !this.#isSpotFresh(spot)) return false;
    if (!this.#spotMatchesSearch(spot, this.searchQuery)) return false;
    return true;
  }

  #getActiveSpots() {
    return this.spots.filter((spot) => this.#spotMatchesDiscoveryFilters(spot));
  }

  /**
   * @param {{ resetBatch?: boolean }} [options]
   */
  #applyDiscoveryFilters(options = {}) {
    const resetBatch = options.resetBatch !== false;
    const activeSpots = this.#getActiveSpots();

    if (resetBatch) {
      this.#visibleListCount = LIST_BATCH_SIZE;
    } else {
      this.#visibleListCount = Math.min(this.#visibleListCount, activeSpots.length);
    }

    this.#renderDiscoveryListEntries(activeSpots);
    this.#syncMapStatus(activeSpots);
    void this.#syncSpotSourceData(activeSpots);
  }

  /**
   * @param {DiscoverySpot[]} activeSpots
   */
  #renderDiscoveryListEntries(activeSpots) {
    const listContainer = this.refs.listContainer;
    if (listContainer instanceof HTMLElement) {
      if (this.apiDataActive) {
        this.#syncApiEntryMatchState(activeSpots);
        return;
      }

      const activeHandles = new Set(activeSpots.map((spot) => spot.handle));
      const matches = [];
      for (const entry of listContainer.querySelectorAll('.discovery-map__list-entry')) {
        if (!(entry instanceof HTMLElement)) continue;
        const handle = entry.dataset.spotHandle;
        const isMatch = Boolean(handle && activeHandles.has(handle));
        entry.dataset.discoveryMatch = isMatch ? 'true' : 'false';
        if (isMatch && handle) matches.push(handle);
      }

      this.#filteredListHandles = matches;
      this.#applyListEntryVisibility();
    } else {
      this.#filteredListHandles = [];
    }
  }

  /** @param {DiscoverySpot[]} spots */
  #renderApiListEntries(spots) {
    const listContainer = this.refs.listContainer;
    if (!(listContainer instanceof HTMLElement)) return;
    const html = `${this.#renderSubmitListRow()}${spots.map((spot) => this.#renderApiListEntry(spot)).join('')}`;
    listContainer.innerHTML = html;
    if (this.#listLoadSentinel) {
      this.#listLoadSentinel.remove();
      this.#listLoadSentinel = null;
    }
    this.#setupListLazyLoad();
  }

  #renderSubmitListRow() {
    const template = this.querySelector('[data-discovery-submit-template]');
    return template instanceof HTMLTemplateElement ? template.innerHTML : '';
  }

  /** @param {DiscoverySpot[]} activeSpots */
  #syncApiEntryMatchState(activeSpots) {
    const listContainer = this.refs.listContainer;
    if (!(listContainer instanceof HTMLElement)) return;
    const activeHandles = new Set(activeSpots.map((spot) => spot.handle));
    const matches = [];
    for (const entry of listContainer.querySelectorAll('.discovery-map__list-entry')) {
      if (!(entry instanceof HTMLElement)) continue;
      const handle = entry.dataset.spotHandle;
      const isMatch = Boolean(handle && activeHandles.has(handle));
      entry.dataset.discoveryMatch = isMatch ? 'true' : 'false';
      if (isMatch && handle) matches.push(handle);
    }
    this.#filteredListHandles = matches;
    this.#applyListEntryVisibility();
  }

  /** @param {DiscoverySpot} spot */
  #renderApiListEntry(spot) {
    const category = this.#spotCategoryLine(spot);
    const location = this.#spotLocationLine(spot);
    const signal = this.#spotSignal(spot, 96);
    const openStatus = this.#openStatusBadge(spot, 'discovery-map__list-status');
    const trustBadges = renderTrustBadges(spot.trustBadges, 'discovery-map__list-status');
    const logo = spot.logoImage || '';
    const title = spot.name || spot.title || '';
    const profileUrl = venueProfileUrl(spot, this.settings?.venueDirectoryPath);
    return `
      <div class="discovery-map__list-entry" data-spot-handle="${escapeAttr(spot.handle)}" ${
        spot.lastContributionAt || spot.lastUpdated
          ? `data-last-updated="${escapeAttr(spot.lastContributionAt || spot.lastUpdated)}"`
          : ''
      }>
        <button
          class="discovery-map__list-item${spot.featured ? ' discovery-map__list-item--featured' : ''}"
          data-spot-handle="${escapeAttr(spot.handle)}"
          ${spot.featured ? 'data-featured="true"' : ''}
          on:click="/handleListItemClick"
          type="button"
        >
          ${
            logo
              ? `<img class="discovery-map__list-item-logo" src="${escapeAttr(logo)}" alt="${escapeAttr(title)}" width="40" height="40" loading="lazy">`
              : `<span class="discovery-map__list-item-logo discovery-map__list-item-logo--fallback">${escapeHtml(title.slice(0, 1).toUpperCase())}</span>`
          }
          <div class="discovery-map__list-item-text">
            <span class="discovery-map__list-item-title">${escapeHtml(title)}</span>
            ${
              category || location
                ? `<span class="discovery-map__list-item-meta">${[
                    category ? escapeHtml(category) : '',
                    location ? escapeHtml(location) : '',
                  ]
                    .filter(Boolean)
                    .join('<span aria-hidden="true"> | </span>')}</span>`
                : ''
            }
            ${signal ? `<span class="discovery-map__list-item-signal">${escapeHtml(signal)}</span>` : ''}
            ${trustBadges}
            ${openStatus}
          </div>
        </button>
        ${
          profileUrl
            ? `<a class="discovery-map__list-more" href="${escapeAttr(profileUrl)}" aria-label="View profile for ${escapeAttr(title)}">Profile</a>`
            : ''
        }
      </div>
    `;
  }

  #openStatusBadge(spot, className) {
    if (!spot || !spot.openStatus || spot.openStatus === 'unknown') return '';
    const status = spot.openStatus === 'open' ? 'open' : 'closed';
    const label = spot.openStatusLabel || (status === 'open' ? 'Open now' : 'Closed now');
    return `<span class="${className} ${className}--${status}">${escapeHtml(label)}</span>`;
  }

  /** @param {DiscoverySpot} spot */
  #spotCategoryLine(spot) {
    return Array.isArray(spot.categories) && spot.categories.length ? spot.categories.join(', ') : spot.category || '';
  }

  /** @param {DiscoverySpot} spot */
  #spotLocationLine(spot) {
    const distance = this.#distanceTextForSpot(spot);
    const place = spot.neighborhood || (Array.isArray(spot.neighborhoods) ? spot.neighborhoods[0] : '') || spot.address || '';
    if (distance && place) return `${distance} | ${place}`;
    return distance || place || '';
  }

  /**
   * @param {DiscoverySpot} spot
   * @param {number} [limit]
   */
  #spotSignal(spot, limit = 120) {
    const first = (/** @type {unknown} */ value) => {
      if (Array.isArray(value)) return value.find((item) => String(item || '').trim()) || '';
      return String(value || '').trim();
    };
    const text =
      first(spot.whyGo) ||
      first(spot.visitSignal) ||
      first(spot.locationSummary) ||
      first(spot.socialPullText) ||
      first(spot.knownFor) ||
      first(spot.goodFor) ||
      first(spot.profileChips) ||
      first(spot.description) ||
      first(spot.shortDescription);
    return this.#truncateText(text, limit);
  }

  /**
   * @param {string} text
   * @param {number} limit
   */
  #truncateText(text, limit) {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value || value.length <= limit) return value;
    return `${value.slice(0, Math.max(0, limit - 1)).trim()}...`;
  }

  /** @param {DiscoverySpot} spot */
  #distanceTextForSpot(spot) {
    if (!this.#userLocation) return '';
    const lat = Number(spot.latitude);
    const lng = Number(spot.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
    const miles = this.#distanceMiles(this.#userLocation.latitude, this.#userLocation.longitude, lat, lng);
    if (miles > 100) return '';
    return `${this.#formatMiles(miles)} away`;
  }

  /** @param {number} miles */
  #formatMiles(miles) {
    if (miles < 0.1) return 'Less than 0.1 mi';
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
  }

  /**
   * @param {number} lat1
   * @param {number} lon1
   * @param {number} lat2
   * @param {number} lon2
   */
  #distanceMiles(lat1, lon1, lat2, lon2) {
    const radiusMiles = 3958.8;
    const toRad = (value) => (value * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return radiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  #applyListEntryVisibility() {
    const listContainer = this.refs.listContainer;
    if (!(listContainer instanceof HTMLElement)) return;

    let shown = 0;
    for (const entry of listContainer.querySelectorAll('.discovery-map__list-entry')) {
      if (!(entry instanceof HTMLElement)) continue;
      const isMatch = entry.dataset.discoveryMatch === 'true';
      if (!isMatch) {
        entry.hidden = true;
        continue;
      }

      shown += 1;
      entry.hidden = shown > this.#visibleListCount;
    }

    if (this.#listLoadSentinel instanceof HTMLElement) {
      this.#listLoadSentinel.hidden = this.#visibleListCount >= this.#filteredListHandles.length;
      if (this.#listLoadSentinel.parentElement !== listContainer) {
        listContainer.appendChild(this.#listLoadSentinel);
      }
    }
  }

  #revealMoreListItems() {
    if (this.#visibleListCount >= this.#filteredListHandles.length) return;
    this.#visibleListCount = Math.min(this.#visibleListCount + LIST_BATCH_SIZE, this.#filteredListHandles.length);
    this.#applyListEntryVisibility();
  }

  /** @param {string} handle */
  #ensureListHandleVisible(handle) {
    const idx = this.#filteredListHandles.indexOf(handle);
    if (idx < 0) return;
    const requiredCount = idx + 1;
    if (requiredCount <= this.#visibleListCount) return;
    this.#visibleListCount = requiredCount;
    this.#applyListEntryVisibility();
  }

  /** @returns {Promise<void>} */
  async #loadMapbox() {
    if (window.mapboxgl) return;

    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = MAPBOX_GL_CSS;
    document.head.appendChild(cssLink);

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = MAPBOX_GL_JS;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Mapbox GL JS'));
      document.head.appendChild(script);
    });
  }

  #initMap() {
    const { mapboxgl } = window;
    if (!mapboxgl || !this.settings) {
      this.#setDiscoveryLoading(false);
      return;
    }

    const container = this.refs.mapContainer;
    if (!container) {
      this.#setDiscoveryLoading(false);
      return;
    }

    container.innerHTML = '';

    mapboxgl.accessToken = this.settings.accessToken;

    const styleUrl = this.#resolvedStyleUrl();

    const mapOptions = {
      container,
      style: styleUrl,
      center: [
        parseFloat(this.settings.defaultLng) || -84.388,
        parseFloat(this.settings.defaultLat) || 33.749,
      ],
      zoom: this.settings.defaultZoom || 12,
      pitch: MAP_PITCH,
      attributionControl: true,
      pitchWithRotate: false,
    };

    if (this.#isStandardStyleUrl(styleUrl)) {
      mapOptions.config = {
        basemap: {
          lightPreset: 'day',
          theme: 'monochrome',
          font: 'Source Serif Pro',
        },
      };
    }

    try {
      this.map = new mapboxgl.Map(mapOptions);
    } catch (e) {
      console.error('Discovery Map: Failed to create map', e);
      this.#setDiscoveryLoading(false);
      return;
    }

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

    if (this.settings.showMyLocation) {
      const geolocate = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      });
      geolocate.on('geolocate', (/** @type {GeolocationPosition} */ event) => {
        this.#handleUserGeolocated(event);
      });
      this.map.addControl(geolocate, 'bottom-right');
    }

    this.map.on('load', () => {
      if (!this.#isConnected) return;
      this.#applyStandardBasemapConfig();
      this.#initSpotLayers();
      this.#applyDiscoveryFilters();
      this.#setDiscoveryLoading(false);
    });

    window.addEventListener('resize', this.#handleResize);
  }

  #handleResize = debounce(() => {
    if (this.map) this.map.resize();
  }, 150);

  /** @param {any} event */
  #handleUserGeolocated(event) {
    const latitude = Number(event?.coords?.latitude);
    const longitude = Number(event?.coords?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    this.#userLocation = { latitude, longitude };
    if (this.apiDataActive) {
      this.#renderApiListEntries(this.spots);
      this.#applyDiscoveryFilters({ resetBatch: false });
    }

    if (this.activeSpotHandle) {
      this.#highlightListItem(this.activeSpotHandle);
      const spot = this.spots.find((s) => s.handle === this.activeSpotHandle);
      if (spot && this.#isPreviewOpen()) this.#showPreviewPanel(spot);
    }
  }

  /** @param {string|undefined} url */
  #isStandardStyleUrl(url) {
    return typeof url === 'string' && url.includes('mapbox://styles/mapbox/standard');
  }

  /** @returns {string} */
  #resolvedStyleUrl() {
    const u = this.settings?.mapStyleUrl;
    return typeof u === 'string' && u.trim() !== '' ? u.trim() : 'mapbox://styles/mapbox/standard';
  }

  #applyStandardBasemapConfig() {
    if (!this.map || !this.#isStandardStyleUrl(this.#resolvedStyleUrl())) return;
    try {
      this.map.setConfigProperty('basemap', 'lightPreset', 'day');
      this.map.setConfigProperty('basemap', 'theme', 'monochrome');
      this.map.setConfigProperty('basemap', 'font', 'Source Serif Pro');
    } catch (e) {
      console.warn('Discovery Map: Standard basemap config not applied', e);
    }
  }

  /**
   * @param {DiscoverySpot[]} spots
   * @returns {{ type: 'FeatureCollection', features: Array<{ type: 'Feature', properties: { handle: string, title: string, label: string, featured: boolean, soAtlantaApproved: boolean, signalRank: number, iconImageId: string }, geometry: { type: 'Point', coordinates: [number, number] } }> }}
   */
  #buildFilteredGeoJson(spots) {
    /** @type {Array<{ type: 'Feature', properties: { handle: string, title: string, label: string, featured: boolean, soAtlantaApproved: boolean, signalRank: number, iconImageId: string }, geometry: { type: 'Point', coordinates: [number, number] } }>} */
    const features = [];
    for (const spot of spots) {
      const lat = parseFloat(String(spot.latitude));
      const lng = parseFloat(String(spot.longitude));
      if (isNaN(lat) || isNaN(lng)) continue;
      const title = spot.name || spot.title || '';
      const soAtlantaApproved = spot.soAtlantaApproved === true;
      features.push({
        type: 'Feature',
        properties: {
          handle: spot.handle,
          title,
          label: title,
          featured: Boolean(spot.featured),
          soAtlantaApproved,
          signalRank: soAtlantaApproved ? 2 : spot.featured ? 1 : 0,
          iconImageId: this.#pinImageIdForSpot(spot),
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      });
    }
    return { type: 'FeatureCollection', features };
  }

  /**
   * @param {DiscoverySpot[]} [activeSpots]
   */
  async #syncSpotSourceData(activeSpots) {
    if (!this.map) return;
    const src = this.map.getSource(DISCOVERY_SOURCE_ID);
    if (!src || typeof src.setData !== 'function') return;

    const runId = ++this.#syncRunId;
    const spots = activeSpots ?? this.#getActiveSpots();
    await this.#ensurePinImagesForSpots(spots);
    if (runId !== this.#syncRunId || !this.map) return;

    const prevActive = this.activeSpotHandle;
    const data = this.#buildFilteredGeoJson(spots);
    const activeStillVisible = Boolean(
      prevActive &&
        data.features.some(
          (/** @type {{ properties?: { handle?: string } }} */ f) => f.properties && f.properties.handle === prevActive
        )
    );

    src.setData(data);

    if (!activeStillVisible) {
      if (prevActive) {
        this.#hidePreviewPanel();
      }
      this.activeSpotHandle = null;
      this.#highlightListItem(null);
      return;
    }

    if (prevActive) {
      try {
        this.map.setFeatureState({ source: DISCOVERY_SOURCE_ID, id: prevActive }, { active: true });
      } catch (_) {
        /* feature state may be unavailable briefly */
      }
    }
  }

  #fallbackPinDataUri() {
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24">' +
      '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="#111111"/>' +
      '<circle cx="12" cy="10" r="3.2" fill="#ffffff"/>' +
      '</svg>';
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }

  /** @param {string} handle */
  #logoPinImageId(handle) {
    return `discovery-pin-logo-${handle}`;
  }

  #ensureFallbackPinImage() {
    if (!this.map) return false;
    if (this.map.hasImage(FALLBACK_PIN_IMAGE_ID)) {
      this.#loadedImageIds.add(FALLBACK_PIN_IMAGE_ID);
      return true;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 48;
      canvas.height = 48;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      ctx.fillStyle = '#111111';
      ctx.beginPath();
      ctx.moveTo(24, 46);
      ctx.bezierCurveTo(21, 42, 6, 31, 6, 20);
      ctx.bezierCurveTo(6, 10, 14, 2, 24, 2);
      ctx.bezierCurveTo(34, 2, 42, 10, 42, 20);
      ctx.bezierCurveTo(42, 31, 27, 42, 24, 46);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(24, 20, 6.5, 0, Math.PI * 2);
      ctx.fill();

      this.map.addImage(FALLBACK_PIN_IMAGE_ID, ctx.getImageData(0, 0, 48, 48));
      this.#loadedImageIds.add(FALLBACK_PIN_IMAGE_ID);
      return true;
    } catch (_) {
      return false;
    }
  }

  /** @param {DiscoverySpot} spot */
  #pinImageIdForSpot(spot) {
    if (!spot.logoImage) return FALLBACK_PIN_IMAGE_ID;
    const logoId = this.#logoPinImageId(spot.handle);
    if (this.#failedLogoImageIds.has(logoId)) return FALLBACK_PIN_IMAGE_ID;
    if (!this.#loadedImageIds.has(logoId)) return FALLBACK_PIN_IMAGE_ID;
    return logoId;
  }

  /**
   * @param {string} imageId
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  #ensureMapImage(imageId, url) {
    if (!this.map || !url) return Promise.resolve(false);
    if (this.map.hasImage(imageId)) {
      this.#loadedImageIds.add(imageId);
      return Promise.resolve(true);
    }

    const inFlight = this.#pendingImageLoads.get(imageId);
    if (inFlight) return inFlight;

    const loadPromise = new Promise((resolve) => {
      this.map?.loadImage(url, (/** @type {Error | null} */ error, /** @type {any} */ image) => {
        this.#pendingImageLoads.delete(imageId);
        if (error || !image || !this.map) {
          resolve(false);
          return;
        }

        try {
          if (!this.map.hasImage(imageId)) {
            this.map.addImage(imageId, image);
          }
          this.#loadedImageIds.add(imageId);
          resolve(true);
        } catch (_) {
          resolve(false);
        }
      });
    });

    this.#pendingImageLoads.set(imageId, loadPromise);
    return loadPromise;
  }

  /**
   * @param {DiscoverySpot[]} spots
   * @returns {Promise<void>}
   */
  async #ensurePinImagesForSpots(spots) {
    const tasks = this.#ensureFallbackPinImage()
      ? []
      : [this.#ensureMapImage(FALLBACK_PIN_IMAGE_ID, this.#fallbackPinDataUri())];

    for (const spot of spots) {
      if (!spot.logoImage) continue;
      const logoId = this.#logoPinImageId(spot.handle);
      if (this.#failedLogoImageIds.has(logoId) || this.#loadedImageIds.has(logoId)) continue;

      tasks.push(
        this.#ensureMapImage(logoId, spot.logoImage).then((ok) => {
          if (!ok) this.#failedLogoImageIds.add(logoId);
          return ok;
        })
      );
    }

    await Promise.all(tasks);
  }

  #initSpotLayers() {
    if (!this.map) return;

    this.map.addSource(DISCOVERY_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: CLUSTER_RADIUS,
      promoteId: 'handle',
    });

    this.map.addLayer({
      id: LAYER_CLUSTERS,
      type: 'circle',
      source: DISCOVERY_SOURCE_ID,
      slot: MAP_LAYER_SLOT,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          'rgb(118, 76, 201)',
          10,
          'rgb(100, 60, 180)',
          30,
          'rgb(80, 45, 150)',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 22, 10, 28, 30, 34],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });

    this.map.addLayer({
      id: LAYER_CLUSTER_COUNT,
      type: 'symbol',
      source: DISCOVERY_SOURCE_ID,
      slot: MAP_LAYER_SLOT,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    this.map.addLayer({
      id: LAYER_UNCLUSTERED_DOT,
      type: 'circle',
      source: DISCOVERY_SOURCE_ID,
      slot: MAP_LAYER_SLOT,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'active'], false],
          '#c9a227',
          ['boolean', ['get', 'soAtlantaApproved'], false],
          '#0c694d',
          '#111111',
        ],
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'active'], false],
          8,
          ['boolean', ['get', 'soAtlantaApproved'], false],
          7,
          6,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': ['case', ['boolean', ['get', 'soAtlantaApproved'], false], '#dff6ee', '#ffffff'],
      },
    });

    this.map.addLayer({
      id: LAYER_UNCLUSTERED,
      type: 'symbol',
      source: DISCOVERY_SOURCE_ID,
      slot: MAP_LAYER_SLOT,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['coalesce', ['get', 'iconImageId'], FALLBACK_PIN_IMAGE_ID],
        'icon-size': [
          'case',
          ['boolean', ['get', 'soAtlantaApproved'], false],
          0.84,
          ['boolean', ['get', 'featured'], false],
          0.78,
          0.7,
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport',
      },
    });

    this.map.addLayer({
      id: LAYER_UNCLUSTERED_LABEL,
      type: 'symbol',
      source: DISCOVERY_SOURCE_ID,
      slot: MAP_LAYER_SLOT,
      minzoom: LABEL_MIN_ZOOM,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-offset': [0, 1.15],
        'text-anchor': 'top',
        'text-max-width': 12,
        'text-padding': 4,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-optional': true,
        'symbol-sort-key': ['get', 'signalRank'],
        'text-pitch-alignment': 'viewport',
        'text-rotation-alignment': 'viewport',
      },
      paint: {
        'text-color': ['case', ['boolean', ['feature-state', 'active'], false], '#111111', '#242424'],
        'text-halo-color': '#ffffff',
        'text-halo-width': ['case', ['boolean', ['feature-state', 'active'], false], 2, 1.4],
        'text-halo-blur': 0.4,
      },
    });

    this.map.on('click', LAYER_CLUSTERS, this.#onClusterClick);
    this.map.on('click', LAYER_UNCLUSTERED_DOT, this.#onUnclusteredClick);
    this.map.on('click', LAYER_UNCLUSTERED, this.#onUnclusteredClick);
    this.map.on('click', LAYER_UNCLUSTERED_LABEL, this.#onUnclusteredClick);
    this.map.on('mouseenter', LAYER_CLUSTERS, this.#onSpotsLayerMouseEnter);
    this.map.on('mouseleave', LAYER_CLUSTERS, this.#onSpotsLayerMouseLeave);
    this.map.on('mouseenter', LAYER_UNCLUSTERED_DOT, this.#onSpotsLayerMouseEnter);
    this.map.on('mouseleave', LAYER_UNCLUSTERED_DOT, this.#onSpotsLayerMouseLeave);
    this.map.on('mouseenter', LAYER_UNCLUSTERED, this.#onSpotsLayerMouseEnter);
    this.map.on('mouseleave', LAYER_UNCLUSTERED, this.#onSpotsLayerMouseLeave);
    this.map.on('mouseenter', LAYER_UNCLUSTERED_LABEL, this.#onSpotsLayerMouseEnter);
    this.map.on('mouseleave', LAYER_UNCLUSTERED_LABEL, this.#onSpotsLayerMouseLeave);
  }

  #flyToDefaultView() {
    if (!this.map || !this.settings) return;
    this.map.flyTo({
      center: [
        parseFloat(String(this.settings.defaultLng)) || -84.388,
        parseFloat(String(this.settings.defaultLat)) || 33.749,
      ],
      zoom: this.settings.defaultZoom || 12,
      pitch: MAP_PITCH,
      duration: 600,
    });
  }

  /**
   * @param {DiscoverySpot} spot
   * @param {number} duration
   */
  #flyToSpot(spot, duration = 700) {
    const lat = parseFloat(String(spot.latitude));
    const lng = parseFloat(String(spot.longitude));
    if (isNaN(lat) || isNaN(lng)) return;

    this.map?.flyTo({
      center: [lng, lat],
      zoom: SPOT_ZOOM,
      pitch: MAP_PITCH,
      duration,
      essential: true,
    });
  }

  /**
   * @param {string} handle
   * @param {{ fly?: boolean }} [options]
   */
  #previewSpot(handle, options = {}) {
    const spot = this.spots.find((s) => s.handle === handle);
    if (!spot) return;

    const lat = parseFloat(String(spot.latitude));
    const lng = parseFloat(String(spot.longitude));
    if (isNaN(lat) || isNaN(lng)) return;

    this.#setActiveMarker(handle);
    this.#highlightListItem(handle);
    this.#showPreviewPanel(spot);

    if (options.fly !== false) {
      this.#flyToSpot(spot, 700);
    }
  }

  /**
   * Focus map on a venue from the sidebar and show the map preview.
   * @param {string} handle
   */
  #focusSpotFromList(handle) {
    this.#previewSpot(handle, { fly: true });
  }

  /** @param {string} handle */
  #setActiveMarker(handle) {
    if (!this.map) {
      this.activeSpotHandle = handle;
      return;
    }
    const src = this.map.getSource(DISCOVERY_SOURCE_ID);
    if (!src) {
      this.activeSpotHandle = handle;
      return;
    }

    if (this.activeSpotHandle && this.activeSpotHandle !== handle) {
      try {
        this.map.removeFeatureState({ source: DISCOVERY_SOURCE_ID, id: this.activeSpotHandle });
      } catch (_) {
        /* ignore */
      }
    }
    this.activeSpotHandle = handle;
    try {
      this.map.setFeatureState({ source: DISCOVERY_SOURCE_ID, id: handle }, { active: true });
    } catch (_) {
      /* ignore */
    }
  }

  #isPreviewOpen() {
    const panel = this.refs.previewPanel;
    return panel instanceof HTMLElement && !panel.hidden;
  }

  /** @param {DiscoverySpot} spot */
  #showPreviewPanel(spot) {
    const panel = this.refs.previewPanel;
    const inner = this.refs.previewPanelInner;
    if (!(panel instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;

    panel.dataset.spotHandle = spot.handle;
    inner.innerHTML = this.#renderPreviewCard(spot);
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
  }

  #hidePreviewPanel() {
    const panel = this.refs.previewPanel;
    const inner = this.refs.previewPanelInner;
    if (inner instanceof HTMLElement) inner.replaceChildren();
    if (!(panel instanceof HTMLElement)) return;
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    delete panel.dataset.spotHandle;
  }

  /** @param {DiscoverySpot} spot */
  #renderPreviewCard(spot) {
    const title = spot.name || spot.title || 'Untitled spot';
    const category = this.#spotCategoryLine(spot);
    const location = this.#spotLocationLine(spot);
    const signal = this.#spotSignal(spot, 150);
    const href = venueProfileUrl(spot, this.settings?.venueDirectoryPath);
    const mapsUrl = this.#mapsUrlForSpot(spot);
    const image = spot.fallbackImage || spot.imageUrl || spot.featuredVideo?.thumbnailUrl || '';
    const logo = spot.logoImage || spot.logoUrl || '';
    const trustBadges = renderTrustBadges(spot.trustBadges, 'discovery-map__preview-badge');
    const chips = this.#spotPreviewChips(spot)
      .map((chip) => `<span class="discovery-map__preview-chip">${escapeHtml(chip)}</span>`)
      .join('');

    return `
      <article class="discovery-map__preview-card${spot.featured ? ' discovery-map__preview-card--featured' : ''}" data-spot-handle="${escapeAttr(spot.handle)}">
        <div class="discovery-map__preview-media">
          ${
            image
              ? `<img class="discovery-map__preview-image" src="${escapeAttr(image)}" alt="${escapeAttr(title)}" loading="lazy">`
              : logo
                ? `<img class="discovery-map__preview-image discovery-map__preview-image--logo" src="${escapeAttr(logo)}" alt="${escapeAttr(title)}" loading="lazy">`
                : `<span class="discovery-map__preview-fallback" aria-hidden="true">${escapeHtml(title.slice(0, 1).toUpperCase())}</span>`
          }
        </div>
        <div class="discovery-map__preview-body">
          <div class="discovery-map__preview-heading">
            ${trustBadges}
            ${spot.featured ? '<span class="discovery-map__preview-badge">Featured</span>' : ''}
            ${this.#openStatusBadge(spot, 'discovery-map__preview-badge discovery-map__preview-badge-status')}
            <h3 class="discovery-map__preview-title">${escapeHtml(title)}</h3>
            ${
              category || location
                ? `<p class="discovery-map__preview-meta">${[
                    category ? escapeHtml(category) : '',
                    location ? escapeHtml(location) : '',
                  ]
                    .filter(Boolean)
                    .join('<span aria-hidden="true"> | </span>')}</p>`
                : ''
            }
          </div>
          ${signal ? `<p class="discovery-map__preview-signal">${escapeHtml(signal)}</p>` : ''}
          ${chips ? `<div class="discovery-map__preview-chips" aria-label="Venue highlights">${chips}</div>` : ''}
          <div class="discovery-map__preview-actions">
            ${
              href
                ? `<a class="discovery-map__preview-action button" href="${escapeAttr(href)}">${this.#renderProfileIcon()}<span>View profile</span></a>`
                : ''
            }
            ${
              mapsUrl
                ? `<a class="discovery-map__preview-action button button-secondary" href="${escapeAttr(mapsUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Open directions for ${escapeAttr(title)}">${this.#renderDirectionsIcon()}<span>Directions</span></a>`
                : ''
            }
          </div>
        </div>
      </article>
    `;
  }

  /** @param {DiscoverySpot} spot */
  #spotPreviewChips(spot) {
    const values = [
      ...(Array.isArray(spot.profileChips) ? spot.profileChips : []),
      ...(Array.isArray(spot.knownFor) ? spot.knownFor : []),
      ...(Array.isArray(spot.goodFor) ? spot.goodFor : []),
    ];
    const seen = new Set();
    return values
      .map((value) => String(value || '').trim())
      .filter((value) => {
        const key = value.toLowerCase();
        if (!value || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  }

  /** @param {DiscoverySpot} spot */
  #mapsUrlForSpot(spot) {
    if (spot.latitude != null && spot.longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${spot.latitude},${spot.longitude}`)}`;
    }
    if (spot.address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.address)}`;
    }
    return '';
  }

  #renderDirectionsIcon() {
    return `
      <svg class="discovery-map__preview-action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 11l1.5-4.5a2 2 0 0 1 1.9-1.4h7.2a2 2 0 0 1 1.9 1.4L19 11M5 11h14M5 11v7a1 1 0 0 0 1 1h1m12-8v7a1 1 0 0 1-1 1h-1m-10 0h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="7.5" cy="16.5" r="1.75" stroke="currentColor" stroke-width="1.5"/>
        <circle cx="16.5" cy="16.5" r="1.75" stroke="currentColor" stroke-width="1.5"/>
      </svg>
    `;
  }

  #renderProfileIcon() {
    return `
      <svg class="discovery-map__preview-action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M8 6h12M8 12h12M8 18h5M4 6h.01M4 12h.01M4 18h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  handlePreviewClose() {
    this.#clearActiveState();
  }

  #clearActiveState() {
    if (this.map && this.activeSpotHandle) {
      try {
        this.map.removeFeatureState({ source: DISCOVERY_SOURCE_ID, id: this.activeSpotHandle });
      } catch (_) {
        /* ignore */
      }
    }
    this.activeSpotHandle = null;
    this.#highlightListItem(null);
    this.#hidePreviewPanel();
  }

  /**
   * @param {Event} event
   * @param {string} selector
   */
  #closestFromEvent(event, selector) {
    const path = event.composedPath?.() || [];
    for (const item of path) {
      if (!(item instanceof Element)) continue;
      if (item.matches(selector)) return item;
      const found = item.closest(selector);
      if (found) return found;
    }
    const target = event.target;
    return target instanceof Element ? target.closest(selector) : null;
  }

  /** @param {string|null} handle */
  #highlightListItem(handle) {
    const listContainer = this.refs.listContainer;
    if (!listContainer) return;
    if (handle) this.#ensureListHandleVisible(handle);

    for (const entry of listContainer.querySelectorAll('.discovery-map__list-entry')) {
      const btn = entry.querySelector('.discovery-map__list-item');
      if (!(btn instanceof HTMLElement)) continue;
      const isActive = entry.dataset.spotHandle === handle;
      btn.classList.toggle('discovery-map__list-item--active', isActive);

      if (isActive) {
        entry.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /** @param {Event} event */
  handleListItemClick(event) {
    const btn = this.#closestFromEvent(event, '.discovery-map__list-item');
    if (!btn) return;

    const handle = btn instanceof HTMLElement ? btn.dataset.spotHandle : '';
    if (!handle) return;

    this.#focusSpotFromList(handle);
  }

  handleResetMap() {
    this.#clearActiveState();

    this.searchQuery = '';
    this.freshOnly = false;

    for (const input of this.#refsToInputArray(this.refs.discoverySearchInput)) {
      input.value = '';
    }
    for (const toggle of this.#refsToInputArray(this.refs.discoveryFreshToggle)) {
      toggle.checked = false;
    }

    this.#applyDiscoveryFilters({ resetBatch: true });
    this.#flyToDefaultView();
  }

}

if (!customElements.get('discovery-map-component')) {
  customElements.define('discovery-map-component', DiscoveryMapComponent);
}
